/**
 * Layout + type class fragments for the home page, built on the Paper
 * artboard "Brew — Home (final)" (1440 wide, 64px gutters), with the content
 * column narrowed to Tailwind's `max-w-5xl` (1024px); the footer runs full
 * width inside the same gutters. Widths use Tailwind's
 * container scale, never arbitrary pixel values. Colors come from the `mk-*`
 * tokens in `app/marketing-theme.css`.
 *
 * ONE spacing scale for every section — sections never re-type these:
 *   - section padding  `MK_SECTION_Y`   96px (64px on phones), so any two
 *                                       sections sit 192px apart
 *   - inside a section `MK_SECTION_GAP` 48px between the header, the
 *                                       content and a trailing CTA
 *   - inside a header  title → lede 12px, copy → CTA row 24px
 *                      (`SectionHeader`)
 *   - lede measure     `MK_LEDE_MEASURE` 672px (`max-w-2xl`)
 *   - surfaces         `MK_SURFACE_RADIUS` 24px for every full-width panel
 *                      and media frame; cards inside them are 16px
 */

/** Section gutters: 64px on desktop, so the 1024px column needs a 1152px viewport. */
export const MK_GUTTER_X = 'px-5 md:px-10 lg:px-16'

/** The content column every section and the hero stage share: 1024px. */
export const MK_CONTAINER = 'mx-auto w-full max-w-5xl'

/** Section padding: 96px top and bottom (64px on phones). */
export const MK_SECTION_Y = 'py-16 md:py-24'

/** A section that belongs to its neighbour (the logo row under the hero). */
export const MK_SECTION_Y_COMPACT = 'py-10 md:py-12'

/** Between a section's header, its content and a trailing CTA: 48px. */
export const MK_SECTION_GAP = 'gap-10 md:gap-12'

/** Every lede's line length. */
export const MK_LEDE_MEASURE = 'max-w-2xl'

/** Every full-width panel and media frame. */
export const MK_SURFACE_RADIUS = 'rounded-3xl'

/*
 * THE type scale. Every line of page copy uses one of these; nothing re-types
 * a size. Headings and UI labels are medium (500), running text regular
 * (400); the page has no semibold or bold (the footer wordmark is a logo, and
 * illustrations keep the product UI's own type). Sizes step 56 · 36 · 24 ·
 * 18 · 16 · 14 · 13 (mobile in parentheses):
 *
 *   hero H1           56 (40)  MK_HERO_TITLE
 *   section H2        36 (28)  MK_SECTION_TITLE
 *   card title, quote 24 (20)  MK_CARD_TITLE
 *   hero subhead      18 (16)  MK_HERO_LEDE
 *   section lede      16       MK_LEDE
 *   body              16       MK_BODY
 *   label             16 medium MK_LABEL (FAQ questions, names, the logo row)
 *   small             14       MK_SMALL (supporting lines in cards)
 *   caption           13       MK_CAPTION (meta: a person's role, units)
 */

/** Hero H1 — 56/60 (40/44), medium. */
export const MK_HERO_TITLE =
  'font-medium font-mk-display text-[40px] text-mk-ink leading-[44px] tracking-[-0.025em] md:text-[56px] md:leading-[60px]'

/** Section H2 — 36/44 (28/34), medium. */
export const MK_SECTION_TITLE =
  'font-medium font-mk-display text-[28px] leading-[34px] tracking-[-0.02em] md:text-[36px] md:leading-[44px]'

/** Card title or pull quote — 24/32 (20/28), medium. */
export const MK_CARD_TITLE =
  'font-medium font-mk-display text-[20px] leading-7 tracking-[-0.015em] md:text-[24px] md:leading-8'

/** Hero subhead — 18/28 (16/26), regular: the one step above a lede. */
export const MK_HERO_LEDE =
  'font-mk-body text-[16px] leading-[26px] tracking-[-0.01em] md:text-[18px] md:leading-7'

/** Running text — 16/26, regular. */
export const MK_BODY =
  'font-mk-body text-[16px] leading-[26px] tracking-[-0.01em]'

/** Section lede — body text, at every width (no bigger desktop step). */
export const MK_LEDE = MK_BODY

/** A short label — 16/24, medium: FAQ questions, names, the logo row's line. */
export const MK_LABEL =
  'font-medium font-mk-body text-[16px] leading-6 tracking-[-0.01em]'

/** A supporting line — 14/22, regular. */
export const MK_SMALL = 'font-mk-body text-[14px] leading-[22px]'

/** Meta text — 13/18, regular: a person's role, a price's unit. */
export const MK_CAPTION = 'font-mk-body text-[13px] leading-[18px]'

/**
 * Native `<details>` that opens like the accordion recipe: the content track
 * grows from 0 to auto over 250ms on the smooth curve (and closes the same
 * way). Progressive: a browser without `::details-content` /
 * `interpolate-size` keeps the instant open, and reduced motion skips it.
 * Keep padding on the content's own element, never on this track.
 */
export const MK_DETAILS_ACCORDION =
  '[interpolate-size:allow-keywords] details-content:h-0 details-content:overflow-clip details-content:transition-[height,content-visibility] details-content:duration-fast details-content:ease-smooth-out details-content:[transition-behavior:allow-discrete] open:details-content:h-auto motion-reduce:details-content:transition-none'

/** Card chrome shared by every illustration card on the page. */
export const MK_CARD =
  'rounded-2xl border border-mk-card-line bg-mk-card shadow-mk-card'

/** The warm rounded panel illustrations sit on (brand guide, deliverability). */
export const MK_PANEL = 'rounded-3xl bg-mk-panel'
